import { FC, useContext, useEffect, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Divider,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import __ from "src/utils/Translations";
import {
  Controller,
  FormProvider,
  useForm,
  SubmitHandler,
  Path,
} from "react-hook-form";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import useCreateArea from "src/Pages/Company/OrganizationStructure/api/useCreateArea";
import { useStructureStore } from "src/Pages/Company/OrganizationStructure/context/useStructureStore";
import { Loader } from "src/Components/Loader/Loader";
import AutocompleteManager from "src/Pages/Company/OrganizationStructure/Areas/Components/AutocompleteManager";
import AutocompleteMainArea from "src/Pages/Company/OrganizationStructure/Areas/Components/AutocompleteMainArea";
import AutocompleteEmployees from "src/Pages/Company/OrganizationStructure/Areas/Components/AutocompleteEmployees";
import {
  ICreateArea,
  IFormData,
} from "src/Pages/Company/OrganizationStructure/Areas/Area.models";
import AutocompleteAdditionalArea from "src/Pages/Company/OrganizationStructure/Areas/Components/AutocompleteAdditionalArea";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { createEditAreaScheme } from "src/Pages/Company/OrganizationStructure/Areas/createEditArea.scheme";
import { dynamicNotification } from "src/utils/Notifications";
import { AxiosError } from "axios";
import {
  BackendError,
  handleBackendErrors,
} from "src/Pages/Company/OrganizationStructure/utils/errors/handleBackendErrors";
import { getErrorMessages } from "src/Pages/Company/OrganizationStructure/utils/errors/errorMessages";
import { MAX_LENGTH_DESCRIPTION } from "src/Pages/Company/OrganizationStructure/Areas/constants/areaDescriptionLimit";
import { GlobalModalContext } from "src/utils/GlobalModal/GlobalModalContext";
import useUnsavedChangesGuard from "src/Pages/Company/OrganizationStructure/utils/useUnsavedChangesGuard";
import useIsAhr from "src/utils/hooks/useIsAhr";

const CreateArea: FC = () => {
  const formMethods = useForm<IFormData>({
    resolver: standardSchemaResolver(createEditAreaScheme),
  });
  const companyId = useCompanyId();
  const history = useHistory();
  const isAhr = useIsAhr();
  const { setModal, resetModal } = useContext(GlobalModalContext);
  const structureId = useStructureStore((state) => state.structureId);
  const [charCount, setCharCount] = useState(0);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const { control, handleSubmit, setError, getValues } = formMethods;
  const { mutate, error } = useCreateArea(companyId, structureId);

  const unblockRef = useRef<(() => void) | null>(null);
  const nextLocationRef = useRef<any>(null);
  const proceedNavigationRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!error) return;

    setIsFormSubmitted(false);

    const errorMessages = getErrorMessages();
    const axiosError = error as AxiosError<{ errors: BackendError[] }>;
    const backendErrors = axiosError.response?.data?.errors || [];
    const processedErrors = handleBackendErrors(backendErrors, errorMessages);

    processedErrors.forEach(({ field, message }) => {
      setError(field as Path<IFormData>, { type: "server", message });
    });
  }, [error, setError]);

  const onSubmit: SubmitHandler<IFormData> = (formData) => {
    const validationResult = createEditAreaScheme.safeParse(formData);

    if (!validationResult.success) {
      dynamicNotification(__("Błąd walidacji formularza"), "error");
      return;
    }

    const payload: ICreateArea = {
      ...formData,
      ...validationResult.data,
      manager:
        formData.manager?.id === "structure"
          ? null
          : formData.manager?.id ?? null,
      parent: formData.parent?.id || null,
      additionalDisplayArea: formData.additionalDisplayArea?.id || null,
      description: formData.description ?? null,
      members: formData.members?.map((member) => member.id) || [],
      inheritManager: formData.manager == null || formData.manager.id == null,
    };

    setIsFormSubmitted(true);

    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }

    mutate(payload);
  };

  const confirmLeaving = useUnsavedChangesGuard({
    onExit: () => {
      if (proceedNavigationRef.current) proceedNavigationRef.current();
    },
    setModal,
    resetModal,
  });

  useEffect(() => {
    unblockRef.current = history.block((tx) => {
      if (isFormSubmitted) return undefined;

      const values = getValues();
      const hasUnsavedChanges = Object.values(values).some(
        (v) => v !== "" && v != null
      );

      if (!hasUnsavedChanges) return undefined;

      nextLocationRef.current = tx;

      proceedNavigationRef.current = () => {
        if (unblockRef.current) unblockRef.current();
        history.push(tx.pathname);
      };

      confirmLeaving();

      return false;
    });

    return () => {
      if (unblockRef.current) unblockRef.current();
    };
  }, [history, getValues, confirmLeaving, isFormSubmitted]);

  if (!structureId) {
    return <Loader />;
  }

  return (
    <FormProvider {...formMethods}>
      <Typography
        variant="h4"
        fontWeight="fontWeightBold"
        textTransform="uppercase"
        sx={{ mb: 3 }}
      >
        {`${__("Tworzenie obszaru")}`}
      </Typography>
      <Paper
        sx={{
          gap: "1.5rem",
          display: "flex",
          flexDirection: "column",
          marginBottom: "1.4rem",
          p: 4,
        }}
      >
        <Typography sx={{ fontSize: "20px" }} fontWeight="fontWeightBold">
          {__("Dodaj obszar")}
        </Typography>
        <Divider sx={{ m: 0 }} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            <Controller
              name="name"
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  fullWidth
                  error={!!error}
                  helperText={error && __(error.message)}
                  label={__("Nazwa obszaru")}
                />
              )}
            />

            <AutocompleteManager
              companyId={companyId!}
              structureId={structureId}
              helperText={__(
                "Jeżeli pozostawisz to pole puste, to zarządzającym tego obszaru stanie się osoba zarządzająca nadrzędnym obszarem."
              )}
            />

            <AutocompleteMainArea
              companyId={companyId}
              structureId={structureId}
            />

            <AutocompleteAdditionalArea
              companyId={companyId}
              structureId={structureId}
            />

            <Controller
              name="description"
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  fullWidth
                  error={!!error}
                  helperText={`${charCount}/${MAX_LENGTH_DESCRIPTION} ${
                    error ? ` - ${__(error.message)}` : ""
                  }`}
                  label={__("Opis")}
                  multiline
                  rows={4}
                  slotProps={{
                    htmlInput: {
                      maxLength: MAX_LENGTH_DESCRIPTION,
                    },
                  }}
                  onChange={(event) => {
                    setCharCount(event.target.value.length);
                    field.onChange(event);
                  }}
                />
              )}
            />

            <Typography fontWeight={700}>
              {__("Dodawanie pracowników")}:
            </Typography>

            <AutocompleteEmployees
              companyId={companyId}
              structureId={structureId}
            />

            <Divider sx={{ m: 0 }} />
            <Box sx={{ display: "flex", gap: "24px" }}>
              <Button
                variant="outlined"
                type="button"
                size="large"
                sx={{ flexGrow: 1 }}
                onClick={() =>
                  history.push(
                    isAhr
                      ? `/ahr/organization-structure/configure-structure/structure-areas`
                      : `/company/edit/${companyId}/organization-structure/configure-structure/structure-areas`
                  )
                }
              >
                {__("Anuluj")}
              </Button>
              <Button
                variant="contained"
                type="submit"
                size="large"
                sx={{ flexGrow: 1 }}
              >
                {__("Stwórz obszar")}
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>
    </FormProvider>
  );
};

export default CreateArea;
